import type { EmployerVerificationCheckpoint, GateFlowStep } from '../api/employerVerificationDashboardApi';

export type GateKey =
  | 'EXISTENCE'
  | 'LINKAGE'
  | 'LEGITIMACY'
  | 'STRUCTURAL_SANITY'
  | 'TEMPORAL';

export const GATE_ORDER_PIPE: GateKey[] = [
  'EXISTENCE',
  'LINKAGE',
  'LEGITIMACY',
  'STRUCTURAL_SANITY',
  'TEMPORAL',
];

export const gateMeta: Record<GateKey, { short: string; title: string; line: string }> = {
  EXISTENCE: {
    short: 'Ex',
    title: 'Existence',
    line: 'Registry & contact presence for this employer.',
  },
  LINKAGE: {
    short: 'Ln',
    title: 'Linkage',
    line: 'Applicant ↔ employer tie and credibility of the tie.',
  },
  LEGITIMACY: {
    short: 'Lg',
    title: 'Legitimacy',
    line: 'Lending-quality signals (identity of the business vs shell risk).',
  },
  STRUCTURAL_SANITY: {
    short: 'St',
    title: 'Role check',
    line: 'Role, pay band, timeline consistency versus policy.',
  },
  TEMPORAL: {
    short: 'Tm',
    title: 'Tenure',
    line: 'Start dates / overlaps aligned with underwriting windows.',
  },
};

export type DossierTabPreset = 'full' | 'companyOnly' | 'locationOnly' | 'none';

export type GateEvidencePolicy = {
  showDossierMap: boolean;
  showDossierBody: boolean;
  dossierTabPreset: DossierTabPreset;
  showOperatorNotes: boolean;
  showRawEvidenceAccordion: boolean;
  evidenceSourceBullets: string[];
  /** Global decision narrative (failReason) — Existence only. */
  showFailReasonAccordion: boolean;
  /** Sidebar suggested actions for this gate; empty uses Existence fallback from final status. */
  suggestedActionBullets: string[];
  /** Shown under "What we evaluated" when API gate flow steps are absent. */
  evaluationLead: string;
};

const GATE_EVIDENCE_POLICIES: Record<GateKey, GateEvidencePolicy> = {
  EXISTENCE: {
    showDossierMap: false,
    showDossierBody: false,
    dossierTabPreset: 'none',
    showOperatorNotes: false,
    showRawEvidenceAccordion: false,
    evidenceSourceBullets: [
      'Business registry and legal presence on file.',
      'Work address, geolocation, and map corroboration.',
      'External lookups (geocode, Places) supporting presence.',
    ],
    showFailReasonAccordion: true,
    suggestedActionBullets: [],
    evaluationLead:
      'Whether the employer exists as a verifiable business with corroborated registry, contact, and location signals.',
  },
  LINKAGE: {
    showDossierMap: false,
    showDossierBody: false,
    dossierTabPreset: 'none',
    showOperatorNotes: false,
    showRawEvidenceAccordion: false,
    evidenceSourceBullets: [
      'Employer identifiers tied to the applicant declaration.',
      'Applicant ↔ employer relationship strength and role fit.',
      'Employment graph confidence for this application.',
    ],
    showFailReasonAccordion: false,
    suggestedActionBullets: [
      'Review employer relationship strength against the application.',
      'Confirm role and employer name consistency with applicant statements.',
    ],
    evaluationLead:
      'Whether the applicant’s declared employer relationship appears credible for this role and application.',
  },
  LEGITIMACY: {
    showDossierMap: false,
    showDossierBody: false,
    dossierTabPreset: 'none',
    showOperatorNotes: false,
    showRawEvidenceAccordion: false,
    evidenceSourceBullets: [
      'Business footprint and shell-risk signals.',
      'Registry consistency and lending-quality employer profile.',
      'Signals that the business is substantive, not a shell entity.',
    ],
    showFailReasonAccordion: false,
    suggestedActionBullets: [
      'Review shell-risk and registry consistency signals.',
      'Escalate if business footprint does not match policy thresholds.',
    ],
    evaluationLead:
      'Whether the employer profile meets lending-quality legitimacy expectations versus shell or thin-entity risk.',
  },
  STRUCTURAL_SANITY: {
    showDossierMap: false,
    showDossierBody: false,
    dossierTabPreset: 'none',
    showOperatorNotes: false,
    showRawEvidenceAccordion: false,
    evidenceSourceBullets: [
      'Role, pay band, and policy consistency checks.',
      'Checkpoint steps stored for this gate.',
    ],
    showFailReasonAccordion: false,
    suggestedActionBullets: [
      'Review role and compensation consistency with policy bands.',
      'Confirm occupation and pay wording align with submitted employment.',
    ],
    evaluationLead:
      'Whether declared role, pay band, and employment structure are internally consistent with policy.',
  },
  TEMPORAL: {
    showDossierMap: false,
    showDossierBody: false,
    dossierTabPreset: 'none',
    showOperatorNotes: false,
    showRawEvidenceAccordion: false,
    evidenceSourceBullets: [
      'Start dates, overlaps, and underwriting windows.',
      'Employment timing signals for tenure and policy windows.',
    ],
    showFailReasonAccordion: false,
    suggestedActionBullets: [
      'Review employment duration and overlap against underwriting windows.',
      'Confirm start dates with applicant documentation if timing is unclear.',
    ],
    evaluationLead:
      'Whether employment timing, tenure, and overlaps align with underwriting policy windows.',
  },
};

export function getGateEvidencePolicy(gate: GateKey): GateEvidencePolicy {
  return GATE_EVIDENCE_POLICIES[gate] ?? GATE_EVIDENCE_POLICIES.EXISTENCE;
}

/** Operator-facing gate outcome for investigation strip and context (from stored gate status). */
export function gateInvestigationOutcomeLabel(raw: string | null | undefined): string {
  const u = (raw ?? '').trim().toUpperCase();
  if (!u || u === 'NOT_RUN') return 'Skipped';
  if (u === 'PASS') return 'Passed';
  if (u === 'FAIL') return 'Failed';
  if (u === 'REVIEW') return 'Requires review';
  if (u === 'NOT_APPLICABLE') return 'Not required';
  return 'Not evaluated';
}

export function checkpointGateKeyFromPublished(cp: EmployerVerificationCheckpoint): GateKey | null {
  const u = cp.gate.trim().toUpperCase();
  return (GATE_ORDER_PIPE as readonly string[]).includes(u) ? (u as GateKey) : null;
}

export function filterPublishedCheckpointsForGate(
  checkpoints: EmployerVerificationCheckpoint[] | undefined,
  gate: GateKey,
): EmployerVerificationCheckpoint[] {
  if (!checkpoints?.length) return [];
  return checkpoints.filter((cp) => checkpointGateKeyFromPublished(cp) === gate);
}

export function gateFlowStepsForGate(steps: GateFlowStep[] | undefined, gate: GateKey): GateFlowStep[] {
  if (!steps?.length) return [];
  const u = gate.toUpperCase();
  return steps.filter((s) => (s.gateKey ?? '').trim().toUpperCase() === u);
}

export function failReasonSummaryLine(full: string, maxLen = 200): string {
  const t = full.trim();
  if (!t) return '';
  if (t.length <= maxLen) return t;
  const slice = t.slice(0, maxLen);
  const re = /[.!?]\s/;
  let best = -1;
  let m: RegExpExecArray | null;
  const r = new RegExp(re.source, 'g');
  while ((m = r.exec(slice)) !== null) {
    best = m.index + 1;
  }
  if (best > 40) return t.slice(0, best).trim();
  return `${slice.trim()}…`;
}

export function gateStatusShort(raw: string | null | undefined): string {
  const u = (raw ?? '').trim().toUpperCase();
  if (!u || u === 'NOT_RUN') return '—';
  if (u === 'PASS') return 'Pass';
  if (u === 'FAIL') return 'Fail';
  if (u === 'REVIEW') return 'Review';
  return u;
}

export function finalOutcomeLabelStored(stored: string | null | undefined): string {
  const u = (stored ?? '').trim().toUpperCase();
  switch (u) {
    case 'VERIFIED':
      return 'Approved';
    case 'REJECTED':
      return 'Not approved';
    case 'MANUAL_REVIEW':
      return 'Needs review';
    default:
      return u || 'Outcome unknown';
  }
}

export function finalOutcomeTechnical(stored: string | null | undefined): string {
  const u = (stored ?? '').trim();
  return u || 'UNKNOWN';
}

export function gateCodeToTitle(code: string): string {
  if (code === 'STRUCTURAL_SANITY') return gateMeta.STRUCTURAL_SANITY.title;
  if (code === 'APPLICANT_SUBMITTED_FIELDS') {
    return 'What the applicant typed (employment)';
  }
  const upper = code.toUpperCase();
  if (upper in gateMeta) return gateMeta[upper as GateKey].title;
  return code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ');
}

export function dossierResolutionLabel(apiValue: string): string {
  switch (apiValue) {
    case 'MATCHED_LOCATION_VERSION':
      return 'Matched saved address';
    case 'GEOCODE_CACHE_ONLY':
      return 'From coordinate cache';
    case 'SNAPSHOT_NORMALIZED_ONLY':
      return 'Address text only';
    case 'MISSING':
      return 'Address not tied to verification';
    default:
      return apiValue.replace(/_/g, ' ');
  }
}

export const dossierHints = {
  refresh:
    'Data is fetched when this page loads. Use Refresh after a verifier re-run or upstream data corrections.',
  mapSingleSite:
    'Map shows only the work address linked to this verification pass — not every site the employer has on file.',
  subtitleLine:
    'One work address tied to this verification · single map marker · manual refresh reloads dossier fields',
  realTimeExplainer:
    'This view does not auto-refresh from the database. Refresh after ingestion or verifier changes.',
} as const;

export const verificationDetailHints = {
  gateFlowLead:
    'Checkpoints always run in this order (left → right). The outlined step is usually the fastest place to start triage.',
  rawJsonLead:
    'Technical payload auditors can copy or diff. Operational review usually stops at checkpoints above.',
} as const;

export const dashboardColumns = {
  decision: 'Final stored decision',
  checkpoints: 'Per-gate results in pipeline order',
  applicationId: 'Loan / application identifier',
  applicantCcId: 'Platform user ID and bank customer identifier (when present)',
  employer: 'Employer legal name from registry linkage',
  attempt: 'Run counter for this application',
  completed: 'When this verification snapshot was persisted',
} as const;
